import { supabase } from '@/integrations/supabase/client';
import { PromoCode, PromoCodeInsert, PromoCodeUpdate } from '@/contracts/promoCodes';

/**
 * Data facade for Promo Codes
 * Following playbook: thin facade, cached by React Query
 */

/**
 * Get active promo codes for a product
 */
export async function getProductPromoCodes(productId: string): Promise<PromoCode[]> {
  const { data, error } = await supabase
    .from('promo_codes')
    .select('*')
    .eq('product_id', productId)
    .or(`expires_at.is.null,expires_at.gte.${new Date().toISOString()}`)
    .order('is_exclusive', { ascending: false })
    .order('discount_value', { ascending: false });
  
  if (error) throw error;
  return (data || []) as PromoCode[];
}

/**
 * Get single promo code by ID
 */
export async function getPromoCode(promoId: string): Promise<PromoCode | null> {
  const { data, error } = await supabase
    .from('promo_codes')
    .select('*')
    .eq('id', promoId)
    .maybeSingle();
  
  if (error) throw error;
  return data as PromoCode | null;
}

/**
 * Get promo code by code string (for validation)
 */
export async function getPromoCodeByCode(code: string, productId: string): Promise<PromoCode | null> {
  const { data, error } = await supabase
    .from('promo_codes')
    .select('*')
    .eq('code', code.toUpperCase())
    .eq('product_id', productId)
    .or(`expires_at.is.null,expires_at.gte.${new Date().toISOString()}`)
    .maybeSingle();
  
  if (error) throw error;
  return data as PromoCode | null;
}

/**
 * Create new promo code (admin only)
 */
export async function createPromoCode(promo: PromoCodeInsert): Promise<PromoCode> {
  const insertData = {
    product_id: promo.product_id,
    code: promo.code.toUpperCase(),
    discount_value: promo.discount_value,
    discount_type: promo.discount_type,
    description: promo.description,
    expires_at: promo.expires_at,
    is_verified: promo.is_verified,
    is_exclusive: promo.is_exclusive,
    vendor_id: promo.vendor_id,
  };
  
  const { data, error } = await supabase
    .from('promo_codes')
    .insert(insertData)
    .select()
    .single();
  
  if (error) throw error;
  return data as PromoCode;
}

/**
 * Update promo code (admin only)
 */
export async function updatePromoCode(promoId: string, updates: PromoCodeUpdate): Promise<void> {
  const updateData = updates.code 
    ? { ...updates, code: updates.code.toUpperCase() }
    : updates;

  const { error } = await supabase
    .from('promo_codes')
    .update(updateData)
    .eq('id', promoId);
  
  if (error) throw error;
}

/**
 * Delete promo code (admin only)
 */
export async function deletePromoCode(promoId: string): Promise<void> {
  const { error } = await supabase
    .from('promo_codes')
    .delete()
    .eq('id', promoId);
  
  if (error) throw error;
}

/**
 * Track promo code click
 */
export async function trackPromoCodeClick(promoId: string): Promise<void> {
  const { error } = await supabase.rpc('increment_promo_clicks', { 
    promo_id: promoId 
  });
  
  if (error) {
    // Fallback if RPC doesn't exist yet - just increment directly
    const { data: current } = await supabase
      .from('promo_codes')
      .select('clicks_count')
      .eq('id', promoId)
      .single();
    
    if (current) {
      await supabase
        .from('promo_codes')
        .update({ clicks_count: (current.clicks_count || 0) + 1 })
        .eq('id', promoId);
    }
  }
}

/**
 * Get all promo codes (admin dashboard)
 */
export async function getAllPromoCodes(): Promise<PromoCode[]> {
  const { data, error } = await supabase
    .from('promo_codes')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return (data || []) as PromoCode[];
}

/**
 * Calculate days remaining until expiration
 */
export function calculateDaysRemaining(expiresAt: string | null): number | null {
  if (!expiresAt) return null;
  
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  
  return diffDays > 0 ? diffDays : 0;
}
